import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { VIDEO_PLAYER_URL, VIDEO_TRACK_PROGRESS } from "../../../../../apis/apis";
import logo from "./../../../../../assests/CF-PPT-1.png";
import CulsightPageLoader from "../../../../../components/CulsightPageLoader";
import { Space } from "antd";

const VideoPage = ({
  video_id,
  title,
  row_id,
  set_current_video_id,
  set_video_api_refresh,
  video_api_refresh,
  last_progress,
  chapter_id, 
  video_row,
  set_course_watch_percent
}) => {

  const videoRef = useRef(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [sessionHash, setSessionHash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [api_disabled, set_api_disabled] = useState(false);

  const lastWatchedTime = useRef(video_row?.watched_seconds || 0);
  const lastApiCallRef = useRef(0);
  const [error_msg, set_error_msg] = useState('');

  //  MULTI-TAB LOCK (only one tab should play/track at a time)


  const tabId = useRef(Math.random().toString(36).slice(2) + Date.now()).current;
  const lockChannelRef = useRef(null);
  // FETCH VIDEO URL + SESSION
  useEffect(() => {
    const fetchVideoUrl = async () => {
      setLoading(true);

      const form = new FormData();
      form.append("video_id", row_id);
      form.append("chapter_id", atob(chapter_id));
      form.append("url", video_id);

      try {
        const res = await VIDEO_PLAYER_URL(form);

        if (res?.data?.status) {
          setVideoUrl(res.data.video_url);
          setSessionId(res.data.session_id);
          setSessionHash(res.data.session_hash);

          if (res.data.watched_seconds !== undefined) {
            lastWatchedTime.current = res.data.watched_seconds;
          }
        }
      } catch (err) {
        console.error("Video URL fetch error", err);
      }

      setLoading(false);
    };

    fetchVideoUrl();
  }, [video_id]);



  //  SEND PROGRESS (MAIN CONTROL POINT)
  const sendProgressToBackend = async () => {
    if (!sessionId || !sessionHash || api_disabled) return;

    const now = Date.now();
    if (now - lastApiCallRef.current < 4000) return;

    lastApiCallRef.current = now;

    const video = videoRef.current;
    if (!video) return;

    const form = new FormData();
    form.append("session_id", sessionId);
    form.append("session_hash", sessionHash);
    form.append("current_time", video.currentTime);

    try {
      const res = await VIDEO_TRACK_PROGRESS(form);
      if (res?.data?.status) {
        if (res?.data?.course_watch_percent) {
          set_course_watch_percent(res.data.course_watch_percent);
          
        }

        //  sync watched seconds from backend
        if (res?.data?.watched_seconds !== undefined) {
          lastWatchedTime.current = res.data.watched_seconds;
        }
        
         set_video_api_refresh(!video_api_refresh);
       
      }else{
          set_error_msg(res?.data?.message)
          set_api_disabled(true)
      }

    } catch (err) {
      console.error("Progress API error", err);

      //  SKIP DETECTED FROM BACKEND
      if (err?.response?.error === "Skipping detected") {

        const allowedTime = err?.response?.data?.allowed_time || 0;

        const video = videoRef.current;
        if (video) {
          video.currentTime = allowedTime;
          video.pause();
        }

        set_api_disabled(true);

        setTimeout(() => {
          set_api_disabled(false);
        }, 10000);
      }
    }
  };

  //  TIME UPDATE
  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) return;

    const current = video.currentTime;

    if (current > lastWatchedTime.current) {
      lastWatchedTime.current = current;
    }

    set_current_video_id(row_id);

    sendProgressToBackend();
  };

  //  HARD SKIP BLOCK
  const handleSeeking = () => {
    const video = videoRef.current;
    if (!video) return;

    const diff = video.currentTime - lastWatchedTime.current;

    if (diff > 5) {
      console.warn("⚠️ Skip attempt blocked");

      video.currentTime = lastWatchedTime.current;
      video.pause();

      set_api_disabled(true);

      setTimeout(() => {
        set_api_disabled(false);
      }, 10000);
    }
  };

  //  MULTI-TAB LOCK SETUP
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;

    const channel = new BroadcastChannel("culsight_video_lock");
    lockChannelRef.current = channel;

    channel.onmessage = (event) => {
      const data = event.data;
      if (!data || data.tabId === tabId || data.type !== "playing") return;

      //  Another tab started playing a video, pause this one and stop tracking
      const video = videoRef.current;
      if (video && !video.paused) {
        video.pause();
      }

      set_api_disabled(true);
      set_error_msg("Video is playing in another tab, so playback/tracking has been paused here.");
    };

    return () => {
      channel.close();
    };
  }, []);

  //  Called when this tab's video starts playing — claims the lock
  const handlePlay = () => {
    if (videoRef.current && videoRef.current.playbackRate <= 1) {
      set_api_disabled(false);
      set_error_msg('');
    }

    lockChannelRef.current?.postMessage({ type: "playing", tabId });
  };

  //  SPEED CONTROL
  const handlePlaybackRateChange = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.playbackRate > 1) {
      //  Speed increase allowed, but tracking pauses while sped up
      set_error_msg('Tracking paused: playback speed increased');
      set_api_disabled(true);
    } else {
      //  Back to normal speed, resume tracking
      set_api_disabled(false);
      set_error_msg('');
    }
  };

  //  HLS SETUP
  useEffect(() => {
    if (!videoUrl) return;

    const video = videoRef.current;
    let hls;

    if (Hls.isSupported()) {
      hls = new Hls({
        maxBufferLength: 30
      });

      hls.loadSource(videoUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.currentTime = lastWatchedTime.current;
        video.play().catch(() => {});
      });

    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = videoUrl;

      video.addEventListener("loadedmetadata", () => {
        video.currentTime = lastWatchedTime.current;
        video.play().catch(() => {});
      });
    }

    video.addEventListener("ratechange", handlePlaybackRateChange);

    return () => {
      if (hls) hls.destroy();
      video.removeEventListener("ratechange", handlePlaybackRateChange);
    };

  }, [videoUrl]);

  //  FULLSCREEN WATERMARK FIX
  useEffect(() => {
    const logoEl = document.getElementById("fullscreen-logo");

    const handleFullscreen = () => {
      const isFullscreen =
        document.fullscreenElement ||
        document.webkitFullscreenElement;

      if (isFullscreen && logoEl) {
        logoEl.style.position = "fixed";
        logoEl.style.top = "20px";
        logoEl.style.left = "20px";
        logoEl.style.width = "120px";
      } else if (logoEl) {
        logoEl.style.position = "absolute";
        logoEl.style.top = "10px";
        logoEl.style.right = "10px";
        logoEl.style.width = "100px";
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreen);
    return () => document.removeEventListener("fullscreenchange", handleFullscreen);
  }, []);

  return loading ? (
    <CulsightPageLoader />
  ) : (
    <div style={{ position: "relative", width: "100%" }}>

      <video
        ref={videoRef}
        onTimeUpdate={handleTimeUpdate}
        onSeeking={handleSeeking}
        onPlay={handlePlay}
        controls
        controlsList="nodownload"
        width="100%"
        style={{
          borderRadius: "10px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
          minHeight: "40vh",
          maxHeight: "80vh",
          objectFit: "contain",
        }}
      />

      {/*  WATERMARK */}
      <img
        id="fullscreen-logo"
        src={logo}
        alt="Watermark"
        style={{
          position: "absolute",
          top: "10px",
          right: "10px",
          opacity: 0.3,
          pointerEvents: "none",
          width: "120px",
        }}
      />

      {/*  FOOTER MARK */}
      <div
        style={{
          position: "absolute",
          bottom: "55px",
          right: "20px",
          fontWeight: "bold",
          color: "rgba(255,255,255,0.5)",
          pointerEvents: "none",
        }}
      >
        © cyberfrat
      </div>

      {/* STATUS */}
      {api_disabled ? (
        <div style={{
          color: "red",
          textAlign: "center",
          fontWeight: "bold",
          marginTop: "5px",
        }}>
           {error_msg}
        </div>
      ) : (
        <div style={{
          color: "#e9c70ada",
          textAlign: "center",
          fontWeight: "bold",
          marginTop: "5px",
        }}>
         
        </div>
      )}

      <Space style={{ marginTop: "10px" }} />
    </div>
  );
};

export default VideoPage;